import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2Icon, Loader2Icon } from 'lucide-react';
import { PageHeader } from '../../components/ui/PageHeader';
import { FormField, inputClasses } from '../../components/ui/FormField';
import { buttonClasses } from '../../utils/buttonStyles';
import { track } from '../../utils/analytics';
type Status = 'idle' | 'submitting' | 'success';
const quickLinks = [{
  to: '/faq',
  label: 'Frequently Asked Questions'
}, {
  to: '/warranty-claim',
  label: 'Start a warranty claim'
}, {
  to: '/order-tracking',
  label: 'Track an order'
}, {
  to: '/returns',
  label: 'Return & Refund Policy'
}, {
  to: '/shipping',
  label: 'Shipping Policy'
}, {
  to: '/warranty',
  label: 'Warranty Information'
}, {
  to: '/safety',
  label: 'Product Safety & Use'
}];
export function Support() {
  const [form, setForm] = useState({
    name: '',
    email: '',
    order: '',
    topic: 'product',
    message: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [status, setStatus] = useState<Status>('idle');
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const next: Record<string, string> = {};
    if (!form.name.trim()) next.name = 'Please enter your name.';
    if (!/^\S+@\S+\.\S+$/.test(form.email)) next.email = 'Please enter a valid email address.';
    if (form.message.trim().length < 10) next.message = 'Please tell us a little more (10+ characters).';
    setErrors(next);
    if (Object.keys(next).length) return;
    setStatus('submitting');
    track('contact_submit', {
      topic: form.topic
    });
    window.setTimeout(() => setStatus('success'), 700);
  };
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setForm(f => ({
    ...f,
    [k]: e.target.value
  }));
  return <>
      <PageHeader title="Contact & Support" lead="Professional-grade lighting deserves professional-grade support. We're here to help before and after your purchase." data-id="element-1327" data-mp="2lucwttw36mym" />
      <div className="mx-auto grid max-w-site gap-14 px-5 py-16 md:px-8 md:py-24 lg:grid-cols-[1fr_1.4fr]" data-id="element-1328" data-mp="f7f15kyy8qik">
        <aside data-id="element-1329" data-mp="k0hubrztdxy8">
          <h2 className="font-display text-2xl font-semibold uppercase" data-id="element-1330" data-mp="k100bgca3qjc">Get help fast</h2>
          <ul className="mt-5 divide-y divide-white/10 border-y mf-hairline" data-id="element-1331" data-mp="69d2vd6lobyz">
            {quickLinks.map(l => <li key={l.to} data-id="element-1332" data-mp="a6raj0emubov">
                <Link to={l.to} className="block py-4 text-white hover:text-ice" data-id="element-1333" data-mp="xy6jhc0wzr93">{l.label}</Link>
              </li>)}
          </ul>
          <h2 className="mt-12 font-display text-2xl font-semibold uppercase" data-id="element-1334" data-mp="12x9ee3n6kh3c">Reach us</h2>
          <dl className="mt-4 space-y-3 text-sm" data-id="element-1335" data-mp="oxi34ao1tet1">
            <div data-id="element-1336" data-mp="twuo0pvd0bsz"><dt className="text-titanium/70" data-id="element-1337" data-mp="twurizukitxq">Email</dt><dd data-id="element-1338" data-mp="tvjkmr5tt62e"><a href="mailto:support@marineforceled.com" className="text-ice hover:underline" data-id="element-1339" data-mp="tvjm6vlh5e4i">support@marineforceled.com</a></dd></div>
          </dl>
        </aside>

        <div className="rounded-2xl border mf-hairline bg-navy p-6 md:p-10" data-id="element-1340" data-mp="3enlqe6u9mcka">
          {status === 'success' ? <div role="status" className="py-10 text-center" data-id="element-1341" data-mp="365a1p9ybw59m">
              <CheckCircle2Icon className="mx-auto h-10 w-10 text-marine" data-id="element-1342" data-mp="324zujtysv8t6" />
              <h2 className="mt-4 font-display text-3xl font-semibold uppercase" data-id="element-1343" data-mp="326su6pazwsro">Message received</h2>
              <p className="mt-2 text-titanium" data-id="element-1344" data-mp="3g24xhl37vrk7">Thanks, {form.name.split(' ')[0]}. Our team will reply to {form.email}.</p>
            </div> : <form noValidate onSubmit={submit} className="grid gap-6 md:grid-cols-2" data-id="element-1345" data-mp="2ngv6sbkw473j">
              <FormField id="name" label="Name" error={errors.name} data-id="element-1346" data-mp="2ihie3a7c85t1">
                <input className={inputClasses} value={form.name} onChange={set('name')} autoComplete="name" data-id="element-1347" data-mp="2x5mx6faayjcc" />
              </FormField>
              <FormField id="email" label="Email" error={errors.email} data-id="element-1348" data-mp="2sdvess1avjxa">
                <input type="email" className={inputClasses} value={form.email} onChange={set('email')} autoComplete="email" data-id="element-1349" data-mp="24p2deg1wsjva" />
              </FormField>
              <FormField id="order" label="Order number (optional)" data-id="element-1350" data-mp="1zpz9iyxo0jow">
                <input className={inputClasses} value={form.order} onChange={set('order')} placeholder="#1001" data-id="element-1351" data-mp="12zx6rt1y8iwy" />
              </FormField>
              <FormField id="topic" label="Topic" data-id="element-1352" data-mp="17vumbx5e1l8o">
                <select className={inputClasses} value={form.topic} onChange={set('topic')} data-id="element-1353" data-mp="1bzafukm5v5h5">
                  <option value="product" data-id="element-1354" data-mp="1gqsf948jcy00">Product question</option>
                  <option value="sizing" data-id="element-1355" data-mp="1gs3ovkonwzz1">Choosing a system</option>
                  <option value="order" data-id="element-1356" data-mp="1lotzvtlm15dl">Order & shipping</option>
                  <option value="warranty" data-id="element-1357" data-mp="1qnnghuw71chz">Warranty</option>
                  <option value="other" data-id="element-1358" data-mp="1qq9m3ozixhxk">Other</option>
                </select>
              </FormField>
              <div className="md:col-span-2" data-id="element-1359" data-mp="1vlp0qj5cvhv1">
                <FormField id="message" label="Message" error={errors.message} data-id="element-1360" data-mp="1zo3it117adl9">
                  <textarea rows={6} className={inputClasses} value={form.message} onChange={set('message')} data-id="element-1361" data-mp="1a0dltoz08zk" />
                </FormField>
              </div>
              <div className="md:col-span-2" data-id="element-1362" data-mp="5d6kn3jwq473">
                <button type="submit" disabled={status === 'submitting'} className={`${buttonClasses('primary')} w-full sm:w-auto`} data-id="element-1363" data-mp="3tx8lvrcgyr58">
                  {status === 'submitting' && <Loader2Icon className="h-4 w-4 animate-spin" data-id="element-1364" data-mp="3p4ffz059lcrk" />}
                  Send message
                </button>
              </div>
            </form>}
        </div>
      </div>
    </>;
}